import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Projects from "./pages/Projects";
import Gallery from "./pages/Gallery";
import Contact from './pages/Contact';
import PageNotFound from "./pages/PageNotFound";
import Header from './components/Header';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CustomCursor from './components/CustomCursor';

import './assets/css/bootstrap.min.css';
import './assets/css/slicknav.min.css';
import './assets/css/swiper-bundle.min.css';
import './assets/css/all.min.css';
import './assets/css/animate.css';
import './assets/css/magnific-popup.css';
import './assets/css/mousecursor.css';
import './assets/css/custom.css';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

import LoaderLogo from './assets/images/era-interiors-logo.png';
gsap.registerPlugin(ScrollTrigger, SplitText);

function App() {
  // ✅ Handle preloader logic
  useEffect(() => {
    const handleLoad = () => {
      const preloader = document.querySelector('.preloader');
      if (preloader) {
        preloader.style.transition = 'opacity 0.6s ease';
        preloader.style.opacity = '0';

        setTimeout(() => {
          preloader.style.display = 'none';
        }, 2000);
      }
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  // ✅ Handle GSAP text animations
  useEffect(() => {
    // Text Effect 1
    const style1Elements = document.querySelectorAll('.text-anime-style-1');
    style1Elements.forEach((element) => {
      const split = new SplitText(element, { type: 'chars, words' });
      gsap.from(split.words, {
        duration: 1,
        delay: 0.5,
        x: 20,
        autoAlpha: 0,
        stagger: 0.05,
        scrollTrigger: {
          trigger: element,
          start: 'top 85%',
        },
      });
    });

    // Text Effect 2
    const style2Elements = document.querySelectorAll('.text-anime-style-2');
    style2Elements.forEach((element) => {
      const split = new SplitText(element, { type: 'chars, words' });
      gsap.from(split.chars, {
        duration: 1,
        delay: 0.1,
        x: 20,
        autoAlpha: 0,
        ease: 'power2.out',
        stagger: 0.03,
        scrollTrigger: {
          trigger: element,
          start: 'top 85%',
        },
      });
    });

    // Text Effect 3
    const style3Elements = document.querySelectorAll('.text-anime-style-3');
    style3Elements.forEach((element) => {
      if (element.animation) {
        element.animation.progress(1).kill();
        element.split?.revert();
      }

      element.split = new SplitText(element, {
        type: 'lines,words,chars',
        linesClass: 'split-line',
      });

      gsap.set(element, { perspective: 400 });
      gsap.set(element.split.chars, { opacity: 0, x: 50 });

      element.animation = gsap.to(element.split.chars, {
        scrollTrigger: {
          trigger: element,
          start: 'top 90%',
        },
        x: 0,
        y: 0,
        rotateX: 0,
        opacity: 1,
        duration: 1,
        ease: 'back.out',
        stagger: 0.02,
      });
    });

    // Cleanup for split text
    return () => {
      document.querySelectorAll('.text-anime-style-3').forEach((element) => {
        if (element.split) {
          element.split.revert();
        }
      });
    };
  }, []);

  return (
    <BrowserRouter>
      {/* ✅ Preloader */}
      <div className="preloader">
        <div className="loading-container">
          <div className="loading"></div>
          <div id="loading-icon">
            <img src={LoaderLogo} alt="Loading..." />
          </div>
        </div>
      </div>

      {/* ✅ Custom cursor */}
      <CustomCursor />

      {/* ✅ App Layout */}
      <Header />
      {/* If Navbar is separate from Header, include it */}
      {/* <Navbar /> */}

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about-us" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/contact-us" element={<Contact />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>

      <Footer />
    </BrowserRouter>
  );
}

export default App;
